import { useLayoutEffect, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import {
  CARAFE_SPOUT,
  COFFEE_OPENING,
  HERO_ANCHORS,
  HERO_SIZE,
  MUG_BATTERY,
  carafePose,
  heroRectToPercent,
} from "./anchors";
import { portraitLayers } from "./layers";
import MugBattery from "./MugBattery";
import type { CoffeePhase } from "./types";

const mug = heroRectToPercent(HERO_ANCHORS.mug);
const MOBILE_CARAFE = "(max-width: 599px)";

function useCarafeVariant() {
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_CARAFE).matches,
  );

  useLayoutEffect(() => {
    const query = window.matchMedia(MOBILE_CARAFE);
    const apply = () => setMobile(query.matches);
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, []);

  return mobile ? "mobile" : "desktop";
}

type PortraitStageProps = {
  phase: CoffeePhase;
  cupFill: number;
  pouring: boolean;
  cupsConsumed: number;
};

type PlatePose = "idle" | "sipping" | "working";

/**
 * All three plates are 1024×1536. Sipping and working share a shoe line at
 * y1436. Idle's soles sit 7px lower because her lean is painted that way,
 * so she shifts up by 7/1536 of the stage. No extra scale: her lower head
 * is the pose, not a smaller character.
 */
const PLATE_SHIFT: Record<PlatePose, string> = {
  idle: "translateY(-0.456%)",
  sipping: "none",
  working: "none",
};

function platePose(phase: CoffeePhase, cupsConsumed: number): PlatePose {
  if (phase === "sipping") return "sipping";
  // 5% and 38% rest. 72% and 100% stay working, including while she is poured another cup.
  if (cupsConsumed < 2) return "idle";
  return "working";
}

export default function PortraitStage({
  phase,
  cupFill,
  pouring,
  cupsConsumed,
}: PortraitStageProps) {
  const reduced = useReducedMotion();
  const pose = platePose(phase, cupsConsumed);
  const sipping = pose === "sipping";
  const showFloorMug = !sipping;
  const carafeVariant = useCarafeVariant();
  const pour = carafePose(carafeVariant);
  const showPour = pouring && showFloorMug;
  const retirePour = phase === "settling" || phase === "sipping" || phase === "energized";
  const calm = Boolean(reduced);
  const poseEase = { duration: calm ? 0 : 0.16, ease: [0.22, 1, 0.36, 1] as const };
  const carafeEase = { duration: calm ? 0 : 0.2, ease: "easeOut" as const };

  return (
    <div className="portrait-slot">
    <div
      className="portrait"
      data-art="layered"
      data-pose={pose}
      style={{
        ["--mug-x" as string]: `${mug.x * 100}%`,
        ["--mug-y" as string]: `${mug.y * 100}%`,
        ["--mug-w" as string]: `${mug.w * 100}%`,
        ["--mug-h" as string]: `${mug.h * 100}%`,
        ["--battery-x" as string]: `${MUG_BATTERY.x * 100}%`,
        ["--battery-y" as string]: `${MUG_BATTERY.y * 100}%`,
        ["--battery-w" as string]: `${MUG_BATTERY.w * 100}%`,
        ["--battery-h" as string]: `${MUG_BATTERY.h * 100}%`,
        ["--open-cx" as string]: `${COFFEE_OPENING.cx * 100}%`,
        ["--open-cy" as string]: `${COFFEE_OPENING.cy * 100}%`,
        ["--open-rx" as string]: `${COFFEE_OPENING.rx * 100}%`,
        ["--open-ry" as string]: `${COFFEE_OPENING.ry * 100}%`,
        ["--fill" as string]: String(cupFill),
      }}
    >
      <motion.div
        key={calm ? "still" : pose}
        className="portrait-poses"
        initial={calm ? false : { y: -4 }}
        animate={{ y: 0 }}
        transition={poseEase}
      >
      <img
        className="portrait-plate"
        src={portraitLayers.poseIdle}
        alt={pose === "idle" ? "Mini REL sitting with her laptop, head resting on her hand." : ""}
        width={HERO_SIZE.width}
        height={HERO_SIZE.height}
        draggable={false}
        aria-hidden={pose !== "idle"}
        style={{ opacity: pose === "idle" ? 1 : 0, transform: PLATE_SHIFT.idle }}
      />
      <img
        className="portrait-plate"
        src={portraitLayers.poseWorking}
        alt={pose === "working" ? "Mini REL sitting upright and typing on her laptop." : ""}
        width={HERO_SIZE.width}
        height={HERO_SIZE.height}
        draggable={false}
        aria-hidden={pose !== "working"}
        style={{ opacity: pose === "working" ? 1 : 0, transform: PLATE_SHIFT.working }}
      />
      <img
        className="portrait-plate"
        src={portraitLayers.poseSipping}
        alt={pose === "sipping" ? "Mini REL drinking from the coffee mug." : ""}
        width={HERO_SIZE.width}
        height={HERO_SIZE.height}
        draggable={false}
        aria-hidden={pose !== "sipping"}
        style={{ opacity: pose === "sipping" ? 1 : 0, transform: PLATE_SHIFT.sipping }}
      />
      </motion.div>

      {showFloorMug ? (
        <div className="mug-stack">
          <img className="layer layer-mug" src={portraitLayers.mugBlank} alt="" />
          <div className="layer layer-liquid" aria-hidden="true">
            <div className="liquid-body" />
          </div>
          <img className="layer layer-mug layer-mug-front" src={portraitLayers.mugFront} alt="" />
          <div className="layer layer-battery">
            <MugBattery cupFill={cupFill} />
          </div>
        </div>
      ) : null}

      <div className="pour-rig" aria-hidden="true" style={{ visibility: retirePour ? "hidden" : "visible" }}>
        <motion.img
          className={showPour ? "pour-stream is-pouring" : "pour-stream"}
          src={portraitLayers.stream}
          alt=""
          style={{
            left: `${((pour.stream.x - pour.stream.w / 2) / HERO_SIZE.width) * 100}%`,
            top: `${(pour.stream.y / HERO_SIZE.height) * 100}%`,
            width: `${(pour.stream.w / HERO_SIZE.width) * 100}%`,
            height: `${(pour.stream.h / HERO_SIZE.height) * 100}%`,
            transformOrigin: "50% 0%",
          }}
          initial={false}
          animate={{ rotate: pour.stream.angle }}
          transition={{ rotate: { duration: 0 } }}
        />
        <motion.img
          className="pour-carafe"
          src={portraitLayers.carafe}
          alt=""
          style={{
            left: `${(pour.box.x / HERO_SIZE.width) * 100}%`,
            top: `${(pour.box.y / HERO_SIZE.height) * 100}%`,
            width: `${(pour.box.w / HERO_SIZE.width) * 100}%`,
            height: `${(pour.box.h / HERO_SIZE.height) * 100}%`,
            transformOrigin: `${CARAFE_SPOUT.x * 100}% ${CARAFE_SPOUT.y * 100}%`,
          }}
          initial={false}
          animate={{
            opacity: showPour ? 1 : 0,
            rotate: showPour ? pour.tilt : pour.tilt + 8,
          }}
          transition={{
            opacity: { duration: calm || retirePour ? 0 : carafeEase.duration, ease: carafeEase.ease },
            rotate: carafeEase,
          }}
        />
      </div>
    </div>
    </div>
  );
}
